import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Fullscreen,
  FilterList,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';

interface ProjectPerformanceChartProps {
  data?: any[];
  loading?: boolean;
  selectedProject?: string;
}

const ProjectPerformanceChart: React.FC<ProjectPerformanceChartProps> = ({
  data,
  loading = false,
  selectedProject = 'all',
}) => {
  // Mock data for demonstration
  const mockData = [
    { month: 'Jan', performance: 85, target: 90, efficiency: 82 },
    { month: 'Feb', performance: 88, target: 90, efficiency: 85 },
    { month: 'Mar', performance: 92, target: 90, efficiency: 94 },
    { month: 'Apr', performance: 89, target: 90, efficiency: 91 },
    { month: 'May', performance: 94, target: 90, efficiency: 87 },
    { month: 'Jun', performance: 96, target: 90, efficiency: 96 },
  ];

  const chartData = data || mockData;
  const currentPerformance = chartData[chartData.length - 1]?.performance || 0;
  const previousPerformance = chartData[chartData.length - 2]?.performance || 0;
  const performanceChange = currentPerformance - previousPerformance;

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Project Performance
          </Typography>
          <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Project Performance
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" fontWeight="bold">
                {currentPerformance}%
              </Typography>
              <Chip
                icon={performanceChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                label={`${performanceChange >= 0 ? '+' : ''}${performanceChange.toFixed(1)}%`}
                color={performanceChange >= 0 ? 'success' : 'error'}
                size="small"
              />
            </Box>
          </Box>
          <Box>
            <IconButton size="small">
              <FilterList />
            </IconButton>
            <IconButton size="small">
              <Fullscreen />
            </IconButton>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="performanceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#1976d2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#1976d2" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="efficiencyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#388e3c" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#388e3c" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              domain={[0, 100]}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="performance"
              stroke="#1976d2"
              strokeWidth={2}
              fill="url(#performanceGradient)"
              name="Performance"
            />
            <Area
              type="monotone"
              dataKey="efficiency"
              stroke="#388e3c"
              strokeWidth={2}
              fill="url(#efficiencyGradient)"
              name="Efficiency"
            />
            <Line
              type="monotone"
              dataKey="target"
              stroke="#f57c00"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Target"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Performance Insights */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            📊 Performance Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {performanceChange >= 0 
              ? `Performance improved by ${performanceChange.toFixed(1)}% this month. Great progress!`
              : `Performance decreased by ${Math.abs(performanceChange).toFixed(1)}% this month. Consider reviewing current strategies.`
            }
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProjectPerformanceChart;
export type { ProjectPerformanceChartProps };